import React, { useCallback, useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { useResizeObserver } from '@wojtekmaj/react-hooks';
import { Document, Page, pdfjs } from 'react-pdf';
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io';
import { MdOutlineFileDownload } from 'react-icons/md';
import { Tooltip } from 'react-tooltip';
import './imagePdfViewer.css';

interface ImgPdfViewerProps {
    onClose: () => void;
    data: string;
    fileType: boolean;
}

const resizeObserverOptions = {};
const maxWidth = 800;

const ImgPdfViewer = (props: ImgPdfViewerProps) => {
    const [numPages, setNumPages] = useState<number>(0);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [containerRef, setContainerRef] = useState<HTMLElement | null>(null);
    const [containerWidth, setContainerWidth] = useState<number>();
    const [containerHeight, setContainerHeight] = useState<number>();

    const onResize = useCallback<ResizeObserverCallback>((entries) => {
        const [entry] = entries;
        if (entry) {
            setContainerWidth(entry.contentRect.width);
            setContainerHeight(entry.contentRect.height);
        }
    }, []);

    useResizeObserver(containerRef, resizeObserverOptions, onResize);

    useEffect(() => {
        pdfjs.GlobalWorkerOptions.workerSrc = new URL(
            'pdfjs-dist/build/pdf.worker.min.mjs',
            import.meta.url,
        ).toString();
    }, []);

    const onDocumentLoadSuccess = ({ numPages }: { numPages: number }): void => {
        setNumPages(numPages);
    };

    const handleScroll = () => {
        if (containerRef) {
            const pages = containerRef.querySelectorAll('.react-pdf__Page');
            for (let i = 0; i < pages.length; i++) {
                const rect = pages[i].getBoundingClientRect();
                if (rect.top >= 0 && rect.top <= window.innerHeight / 2) {
                    setPageNumber(i + 1);
                    break;
                }
            }
        }
    };

    useEffect(() => {
        const container = containerRef;
        if (container) {
            container.addEventListener('scroll', handleScroll);
        }
        return () => {
            if (container) {
                container.removeEventListener('scroll', handleScroll);
            }
        };
    }, [containerRef, numPages]);

    const downloadFile = () => {
        const downloadLink = document.createElement('a');
        downloadLink.href = props.data;
        if (props.fileType) {
            downloadLink.download = 'download.pdf';
        } else {
            downloadLink.download = 'download.jpg';
        }
        downloadLink.click();
    };

    return (
        <div style={{ height: '100%' }}>
            <div className='modal-heading'>
                <h4>{props.fileType ? 'SPA' : 'Emirates ID'}</h4>
                <div className='modal-header-button-container'>
                    <MdOutlineFileDownload className='download-button' id='my-anchor-element-id' onClick={downloadFile} />
                    <Tooltip
                        anchorSelect='#my-anchor-element-id'
                        content='Download'
                        className='custom-tooltip'
                        place='left'
                    />
                    <CloseIcon className='modal-close-button' onClick={props.onClose} />
                </div>
            </div>

            <div className='modal-body'>
                {props.fileType ? (
                    <div style={{ height: '100%' }}>
                        <div className='pdf-view-container' ref={setContainerRef} style={{ overflowY: 'scroll', height: '500px' }}>
                            <Document file={`${props.data}`} onLoadSuccess={onDocumentLoadSuccess}>
                                {Array.from(new Array(numPages), (el, index) => (
                                    <Page
                                        key={`page_${index + 1}`}
                                        pageNumber={index + 1}
                                        scale={1}
                                        width={containerWidth ? Math.min(containerWidth, maxWidth) : maxWidth}
                                        renderTextLayer={false}
                                        renderAnnotationLayer={false}
                                    />
                                ))}
                            </Document>
                        </div>
                        <div style={{ marginTop: '10px', textAlign: 'center' }}>
                            <span style={{ margin: '0 15px' }}>
                                {pageNumber} / {numPages}
                            </span>
                        </div>
                    </div>
                ) : (
                    <div style={{ overflow: 'auto', height: '100%' }}>
                        <img src={props.data} alt='Description' style={{ width: '100%', height: 'auto' }} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default ImgPdfViewer;
